import React from 'react';
import { Dialog } from '../ui/feedback/Dialog.jsx';
import { api } from '../../api.js';
import { tFor } from '../../i18n/i18n.js';

// Policies are public and change infrequently. Keep one request per merchant
// and locale for the lifetime of the page so reopening the dialog does not
// hit the API again. Storing the promise also deduplicates concurrent opens.
// Failed requests are removed below so a later click can retry.
const refundPolicyCache = new Map();

// PolicyDialog — lazy-loads the refund policy text or the contact card
// from the server and renders it inside the existing Dialog primitive.
// The `kind` prop decides which endpoint to hit and which i18n keys to
// use. Keeps the booking flow on the page (no full-page redirect) so a
// curious customer can read the policy and dismiss without losing
// their slot.
//
// The two modes are similar enough that one component handles both —
// the conditional branches are smaller than the duplicated boilerplate
// would be.

export function PolicyDialog({ kind, slug, locale, shop, onClose }) {
  const t = tFor(locale);
  const [state, setState] = React.useState({ status: 'loading' });

  React.useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ status: 'loading' });
    const fetcher = kind === 'refund' ? refundFetcher : contactFetcher;
    fetcher(slug, locale)
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'ok', data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ status: 'error', error: err.message || 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [kind, slug, locale]);

  const title =
    kind === 'refund'
      ? t('refund.heading')
      : t('contact.heading').replace('{shop}', shop?.name || '');

  return (
    <Dialog
      open
      onClose={onClose}
      onConfirm={onClose}
      confirmLabel={t(kind === 'refund' ? 'refund.close' : 'contact.close')}
      hideCancel
      title={title}
    >
      {state.status === 'loading' && (
        <div className="sp-policy__loading">
          {t(kind === 'refund' ? 'refund.loading' : 'contact.loading')}
        </div>
      )}
      {state.status === 'error' && (
        <div className="sp-policy__error">
          {t(kind === 'refund' ? 'refund.failed' : 'contact.failed')}
        </div>
      )}
      {state.status === 'ok' && kind === 'refund' && (
        <RefundBody data={state.data} locale={locale} t={t} />
      )}
      {state.status === 'ok' && kind === 'contact' && (
        <ContactBody data={state.data} t={t} />
      )}
    </Dialog>
  );
}

function RefundBody({ data, locale, t }) {
  // The server returns the policy as { title, intro, tiers, processingTime,
  // contactLine, bodyText } (see server/src/lib/refundPolicy.ts). The
  // tiers already arrive pre-localized as { label, refund } pairs, so
  // we render them verbatim — no client-side bucket mapping needed.
  //
  // We also render `bodyText` (the server's pre-built sentence that
  // weaves together processing time + contact line) and *not* the
  // raw `processingTime`/`contactLine` fields separately. Rendering
  // both would print the same content twice.
  const tiers = Array.isArray(data?.tiers) ? data.tiers : [];
  return (
    <div className="sp-policy">
      {data?.intro && <p className="sp-policy__intro">{data.intro}</p>}
      {tiers.length > 0 && (
        <ul className="sp-policy__tiers">
          {tiers.map((tier, i) => (
            <li key={i}>
              <span className="sp-policy__tier-label">{tier.label}</span>
              <span className="sp-policy__tier-refund">
                <strong>{tier.refund}</strong>
              </span>
            </li>
          ))}
        </ul>
      )}
      {data?.bodyText && (
        <p className="sp-policy__body">{data.bodyText}</p>
      )}
    </div>
  );
}

function ContactBody({ data, t }) {
  // The server returns { address, phone, email } with whatever the
  // merchant has populated. Show only the fields that are present so
  // a missing phone doesn't render an empty row.
  const rows = [
    { label: t('contact.address'), value: data?.address },
    { label: t('contact.phone'), value: data?.phone, href: data?.phone ? `tel:${data.phone}` : null },
    { label: t('contact.email'), value: data?.email, href: data?.email ? `mailto:${data.email}` : null },
  ].filter((r) => r.value);
  if (rows.length === 0) {
    return (
      <div className="sp-policy">
        <p style={{ color: 'var(--text-muted)' }}>—</p>
      </div>
    );
  }
  return (
    <dl className="sp-contact">
      {rows.map((r) => (
        <div key={r.label} className="sp-contact__row">
          <dt>{r.label}</dt>
          <dd>
            {r.href ? (
              <a href={r.href} className="sp-contact__link">
                {r.value}
              </a>
            ) : (
              r.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

async function refundFetcher(slug, locale) {
  const key = `${slug}:${locale}`;
  let request = refundPolicyCache.get(key);
  if (!request) {
    request = api.refundPolicy(slug, locale).catch((err) => {
      if (refundPolicyCache.get(key) === request) {
        refundPolicyCache.delete(key);
      }
      throw err;
    });
    refundPolicyCache.set(key, request);
  }
  return request;
}

async function contactFetcher(slug, locale) {
  return api.contact(slug, locale);
}
